import React from "react";
import { EmailTemplate } from "@common/components/email-template";
import { IPatientInviteFlow, PatientInviteFlowProps, PatientInviteStep, PatientInviteStepConfig } from "./types";
import { useTemplateController } from "./template";
import { I18n } from "react-redux-i18n";

import { PedigreeListDto } from "@common/dto/pedigree/list-dto";
import { PedigreeChooser } from "../components/pedigree-chooser/pedigree-chooser";
import { GetPedigreesRequestParams } from "@modules/invite-status/services/pedigrees";
import { getFilters } from "@common/actions/Filters";
import { FolderPrimaryListDto } from "@common/dto/folder/primary-list-dto";
import { FieldListDto } from "@common/dto/field/list-dto";
import { ColumnListDto } from "@common/dto/column/list-dto";
import { ColumnSaveDto } from "@common/dto/column/save-dto";
import { AdminSmartListFunctionGetDto } from "@common/dto/admin-function/get";
import { HttpErrorDto } from "@common/dto/http";
import { QuestionnaireType } from "@common/dto/questionnaire/questionnaire-type";
import { FhqInviteRelativesGetParams } from "@modules/invite-status/services/invites";
import { InviteStatusIndividualListDto, InviteStatusIndividual } from "@modules/invite-status/dto/individuals/list-dto";
import { RelativeChooser } from "../components/fhq-invite-relative-chooser";
import { InviteListDto } from "@common/dto/invite/list-dto";
import { InviteHistoryDto } from "@common/dto/invite-history/list-dto";
import { InviteDatasheetAdditionalDataUpdateDto } from "@common/dto/invite/datasheet/additional-data-update-dto";
import { isEmail } from "@common/utils/email";
import { InviteMetadataListDto } from "@modules/invite-status/dto/invite/new-invite-check/list-dto";

export type MembersOfOneFamilyFlowBasicProps = {
  saveAdditionalDatasheetFormatData: (data: InviteDatasheetAdditionalDataUpdateDto) => Promise<void>;
  getAdminSmartListFunctions: () => Promise<AdminSmartListFunctionGetDto>;
  savePedigreesColumns: (columns: Array<ColumnSaveDto>) => Promise<void>;
  getPedigreesColumns: () => Promise<Array<ColumnListDto>>;
  getFolderFieldsForAddingPedigreeColumn: (params: {
    searchQuery?: string | undefined;
    folderId?: number | undefined;
    system: boolean;
    contextId: number;
  }) => Promise<Array<FieldListDto>>;
  getFoldersForAddingPedigreeColumn: (params: {
    folderTypeId: number;
    setUp: {
      rootFolderName: string;
      rootFolderImgUrl: string;
    };
  }) => Promise<Array<FolderPrimaryListDto>>;
  getPedigrees: (params: GetPedigreesRequestParams) => Promise<{ data: Array<PedigreeListDto>; totalCount: number }>;
  getIndividualHistoryLogs: (params: {
    inviteId?: InviteListDto["id"];
    globalId: InviteStatusIndividual["id"];
    fhq4?: InviteListDto["fhq4"];
  }) => Promise<Array<InviteHistoryDto>>;
  getPedigreeMembersCount: (pedigreeId: PedigreeListDto["id"]) => Promise<{ data: number }>;
  getFhqInviteRelatives: (params: FhqInviteRelativesGetParams) => Promise<InviteStatusIndividualListDto>;
  showInfoMessage: (message: string) => void;
  getInviteMetadata: (params: { familyId: number; globalId: string }) => Promise<InviteMetadataListDto>;
};

export type MembersOfOneFamilyFlowProps = PatientInviteFlowProps & MembersOfOneFamilyFlowBasicProps;

export const useMembersOfOneFamilyFlow = (props: MembersOfOneFamilyFlowProps): IPatientInviteFlow => {
  const {
    showErrorMessage,
    onSubmit,
    setCurrentStep,
    questionnaire,
    getIndividualHistoryLogs,
    getPedigrees,
    getFoldersForAddingPedigreeColumn,
    getFolderFieldsForAddingPedigreeColumn,
    savePedigreesColumns,
    getPedigreesColumns,
    getAdminSmartListFunctions,
    saveAdditionalDatasheetFormatData,
    getPedigreeMembersCount,
    showInfoMessage,
    getFhqInviteRelatives,
    getInviteMetadata,
  } = props;
  const { emailTemplate, emailSubject, setEmailSubject, setEmailTemplate } = useTemplateController();
  const [selectedIndividuals, setSelectedIndividuals] = React.useState<Array<InviteStatusIndividual>>([]);
  const [selectedPedigree, setSelectedPedigree] = React.useState<PedigreeListDto | undefined>(undefined);

  const [submitting, setSubmitting] = React.useState(false);

  React.useEffect(() => {
    setEmailSubject(questionnaire?.emailTemplate.subject || "");
    setEmailTemplate(questionnaire?.emailTemplate.body || "");
  }, [questionnaire]);

  const handleGetPedigreeMembersCount = React.useCallback(
    async (pedigreeId: PedigreeListDto["id"]) => {
      try {
        const count = await getPedigreeMembersCount(pedigreeId);
        return count.data;
      } catch (err) {
        console.error(err);

        const errorMessage = (err as HttpErrorDto)?.entity || I18n.t("Failed to get pedigree members count");
        showErrorMessage(errorMessage);
      }
    },
    [showErrorMessage, getPedigreeMembersCount]
  );

  const handlePedigreeChooserSubmit = React.useCallback(async () => {
    const membersCount = await handleGetPedigreeMembersCount(selectedPedigree?.id!);

    const canNotInviteMessage = `${I18n.t("youCantInviteMembersToFamilyHistoryQuestionnaire")} ${I18n.t(
          "inOneOfPreviousStepsChooseIndividualQuestionnaire"
        )}`;

    if (typeof membersCount !== "number") {
      return;
    }

    if (membersCount > 1) {
      const isFamilyType = questionnaire?.questionnaireType === QuestionnaireType.family;
      const isNotIndividual = questionnaire?.questionnaireType !== QuestionnaireType.individual;
    
      if ((questionnaire?.fhq4 && isNotIndividual) || (!questionnaire?.fhq4 && isFamilyType)) {
        showErrorMessage(canNotInviteMessage);
        return;
      }
    }
    
    setCurrentStep(PatientInviteStep.individualChooser);
  }, [selectedPedigree, showInfoMessage, questionnaire]);

  const stepsConfig: Partial<Record<PatientInviteStep, PatientInviteStepConfig>> = {
    [PatientInviteStep.pedigreeChooser]: {
      submitBtnProps: {
        disabled: !selectedPedigree,
      },
      component: (
        <PedigreeChooser
          getAdminSmartListFunctions={getAdminSmartListFunctions}
          getPedigreesColumns={getPedigreesColumns}
          savePedigreesColumns={savePedigreesColumns}
          getFolderFields={getFolderFieldsForAddingPedigreeColumn}
          getFolders={getFoldersForAddingPedigreeColumn}
          showErrorMessage={showErrorMessage}
          getFilters={getFilters}
          getPedigrees={getPedigrees}
          selectedPedigree={selectedPedigree}
          onChange={setSelectedPedigree}
        />
      ),
      onGoToPreviousStep: () => {
        setSelectedPedigree(undefined);
      },
      previousStep: PatientInviteStep.receiverChooser,
      onSubmit: handlePedigreeChooserSubmit,
    },
    [PatientInviteStep.individualChooser]: {
      submitBtnProps: {
        disabled: !selectedIndividuals.length,
        // TODO extend validation on email, first name, last name
      },
      component: questionnaire && selectedPedigree && (
        <RelativeChooser
          saveAdditionalDatasheetFormatData={saveAdditionalDatasheetFormatData}
          questionnaire={questionnaire}
          showErrorMessage={showErrorMessage}
          selectedIndividuals={selectedIndividuals}
          getHistoryLogs={getIndividualHistoryLogs}
          getFhqInviteRelatives={(params: { start: number }) =>
            getFhqInviteRelatives({
              id: questionnaire.questionnaireId,
              fhq4: questionnaire.fhq4,
              familyId: selectedPedigree.id,
              start: params.start,
              length: 50,
            })
          }
          onChange={setSelectedIndividuals}
        />
      ),
      onGoToPreviousStep: () => {
        setSelectedPedigree(undefined);
        setSelectedIndividuals([]);
      },
      previousStep: PatientInviteStep.pedigreeChooser,
      submitting,
      onSubmit: async () => {
        const validations = [
          {
            isValid: (selectedIndividual: InviteStatusIndividual) =>
              selectedIndividual.firstName && selectedIndividual.lastName && selectedIndividual.emailAddress,
            msg: I18n.t("Please provide first name, last name and email"),
          },
          {
            isValid: (selectedIndividual: InviteStatusIndividual) => isEmail(selectedIndividual.emailAddress),
            msg: I18n.t("Please provide a valid email"),
          },
        ];

        if (!selectedIndividuals.length) {
          return;
        }

        let invalidValidation:
          | { msg: string; isValid: (selectedIndividual: InviteStatusIndividual) => boolean }
          | undefined;

        selectedIndividuals.find((selectedIndividual) => {
          invalidValidation = validations.find((v) => !v.isValid(selectedIndividual));
          return invalidValidation;
        });

        if (invalidValidation) {
          showErrorMessage(invalidValidation.msg);

          return;
        }

        try {
          setSubmitting(true);

          const isNotValidIndividuals: Array<boolean> = [];

          if (questionnaire?.questionnaireType === QuestionnaireType.family) {
            await Promise.all(
              selectedIndividuals.map(async (selectedIndividual) => {
                const familyData = await getInviteMetadata({
                  familyId: selectedIndividual.familyId,
                  globalId: selectedIndividual.id,
                });
  
                isNotValidIndividuals.push(
                  familyData.some((data) => !data.proband && !data.singlet && data.pedigreeMemberCount != 1)
                );
              })
            );
          }

          if (isNotValidIndividuals.some((isNotValid) => isNotValid)) {
            showErrorMessage(
              `${I18n.t(
                "You cannot invite some of selected individual(s) to a Family History Questionnaire"
              )}. ${I18n.t("selectIndividualForSendInvite")}. ${I18n.t(
                "This problem applies to the following individual(s)"
              )}`
            );
            return;
          }
          setCurrentStep(PatientInviteStep.emailTemplate);
        } catch (err) {
          console.error(err);
          showErrorMessage((err as HttpErrorDto)?.entity || I18n.t("Failed to check individual"));
        } finally {
          setSubmitting(false);
        }
      },
    },
    [PatientInviteStep.emailTemplate]: {
      component: (
        <EmailTemplate
          subject={emailSubject}
          onSubjectChange={setEmailSubject}
          template={emailTemplate}
          onTemplateChange={setEmailTemplate}
        />
      ),
      submitBtnProps: {
        text: I18n.t("Send an Invite"),
      },
      previousStep: PatientInviteStep.individualChooser,
      onSubmit: async () => {
        if (!selectedIndividuals.length || !questionnaire) {
          return;
        }

        try {
          setSubmitting(true);
          await onSubmit({
            globalIds: selectedIndividuals.map((individual) => individual.id),
            fhq4: questionnaire.fhq4,
            questionnaireId: questionnaire.questionnaireId,
            emailSubject,
            emailBody: emailTemplate,
          });
        } catch (err) {
          console.error(err);
          showErrorMessage((err as HttpErrorDto)?.entity || I18n.t('Failed to send an invite'));
        } finally {
          setSubmitting(false);
        }
      },
    },
  };

  return {
    stepsConfig,
    getFirstStep: () => PatientInviteStep.pedigreeChooser,
  };
};
